import { useEffect, useState } from "react";
import axios from "axios";
import { UserX, Send, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../config";
import { CountryFlag } from "../shared/CountryFlag";
import { useCountryFilter } from "../../contexts/CountryFilterContext";

/**
 * Orphan Customers Widget
 *
 * Lists customers without a `dealer_id` association and lets the super_admin
 * adopt each one into a target dealer. Also exposes a "Recompute counters"
 * shortcut to reconcile `dealer.customers_count` with the real DB state.
 *
 * Backed by:
 *   GET  /api/admin/orphan-customers
 *   POST /api/admin/orphan-customers/{id}/assign-dealer
 *   POST /api/admin/dealers/{id}/recompute-counters
 *
 * Req. Zorzutti incident report 26/05/2026.
 */
export const OrphanCustomersWidget = () => {
  const [orphans, setOrphans] = useState({ count: 0, total: 0, items: [] });
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  const [assignTo, setAssignTo] = useState({}); // customerId -> dealerId
  const [recomputeFor, setRecomputeFor] = useState("");
  const [recomputing, setRecomputing] = useState(false);
  const { country, isFiltered } = useCountryFilter();

  const fetchAll = async () => {
    setLoading(true);
    try {
      // Scope orphans to the selected country when the global filter
      // is active. Dealer list stays unfiltered because the operator
      // may still want to reassign the orphan to a dealer in another
      // country (rare but legitimate, e.g. cross-border accounts).
      const orphanParams = { limit: 200, ...(isFiltered ? { country } : {}) };
      const [o, d] = await Promise.all([
        axios.get(`${API}/admin/orphan-customers`, { params: orphanParams }),
        axios.get(`${API}/dashboard/dealers`),
      ]);
      setOrphans(o.data);
      const list = d.data?.dealers || d.data || [];
      setDealers(list);
    } catch {
      setOrphans({ count: 0, total: 0, items: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  const handleAssign = async (customerId) => {
    const dealerId = assignTo[customerId];
    if (!dealerId) {
      toast.error("Seleziona un dealer di destinazione");
      return;
    }
    if (!window.confirm("Confermare l'assegnazione di questo cliente al dealer selezionato? L'azione è auditata e non reversibile da questa schermata.")) return;
    setAssigning(customerId);
    try {
      await axios.post(`${API}/admin/orphan-customers/${customerId}/assign-dealer`, {
        dealer_id: dealerId,
        actor: "super_admin",
      });
      toast.success("Cliente assegnato al dealer");
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'assegnazione");
    } finally {
      setAssigning(null);
    }
  };

  const handleRecompute = async () => {
    if (!recomputeFor) { toast.error("Seleziona un dealer"); return; }
    setRecomputing(true);
    try {
      const r = await axios.post(`${API}/admin/dealers/${recomputeFor}/recompute-counters`, { actor: "super_admin" });
      const drift = r.data.drift;
      toast.success(`Counter aggiornato: ${r.data.previous_count} → ${r.data.new_count} (drift: ${drift > 0 ? "+" : ""}${-drift})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setRecomputing(false);
    }
  };

  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="orphan-customers-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
            <UserX size={20} className="text-orange-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Clienti Orfani · Recovery</h3>
            <p className="text-gray-500 text-xs">Clienti senza dealer_id — assegnali al dealer corretto</p>
          </div>
        </div>
        <button
          onClick={fetchAll}
          className="p-1.5 rounded-lg border border-gray-700 hover:bg-gray-800"
          data-testid="orphan-refresh"
          title="Aggiorna"
        >
          <RefreshCw size={14} className={`text-gray-400 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Recompute counters quick-tool */}
      <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-3 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <span className="text-gray-400 text-xs flex-shrink-0">Ricalcola contatore clienti per dealer:</span>
          <select
            value={recomputeFor}
            onChange={(e) => setRecomputeFor(e.target.value)}
            className="bg-[#0A0A0A] border border-gray-700 rounded px-2 py-1 text-xs text-white flex-1 min-w-0"
            data-testid="recompute-dealer-select"
          >
            <option value="">-- Seleziona dealer --</option>
            {dealers.map(d => (
              <option key={d.id} value={d.id}>
                {d.first_name} {d.last_name} ({d.email}) — counter: {d.customers_count}
              </option>
            ))}
          </select>
          <button
            onClick={handleRecompute}
            disabled={recomputing || !recomputeFor}
            className="px-3 py-1.5 rounded-lg border border-blue-500/40 text-blue-300 text-xs hover:bg-blue-500/10 disabled:opacity-40"
            data-testid="recompute-trigger"
          >
            {recomputing ? "..." : "Ricalcola"}
          </button>
        </div>
      </div>

      {/* Orphan list */}
      <div className="flex items-center justify-between mb-3">
        <p className="text-gray-300 text-sm font-medium">
          Clienti senza dealer
          <span className={`ml-2 px-2 py-0.5 rounded-md text-xs ${orphans.total > 0 ? "bg-orange-500/10 text-orange-300" : "bg-lojack-blue-500/10 text-lojack-blue-300"}`}>
            {orphans.total}
          </span>
        </p>
      </div>

      {orphans.total === 0 ? (
        <div className="text-center py-6">
          <CheckCircle2 size={28} className="text-lojack-blue-500 mx-auto mb-2 opacity-60" />
          <p className="text-gray-500 text-xs">Nessun cliente orfano. Tutti i clienti sono associati a un dealer.</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {orphans.items.map((c) => {
            const displayName = c.company_name
              || `${c.first_name || ""} ${c.last_name || ""}`.trim()
              || "(senza nome)";
            const isBusiness = (c.customer_type || 'private') === 'business';
            const badgeCls = isBusiness
              ? 'bg-lojack-clause-500/15 text-lojack-clause-300 border-lojack-clause-500/40'
              : 'bg-sky-500/15 text-sky-300 border-sky-500/40';
            const dotCls = isBusiness ? 'bg-lojack-clause-400' : 'bg-sky-400';
            const label = isBusiness ? 'Business' : 'Privato';
            return (
              <div key={c.id} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-3" data-testid={`orphan-row-${c.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white text-sm truncate font-medium">{displayName}</p>
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[9px] font-semibold uppercase tracking-wide ${badgeCls}`}
                        data-testid={`orphan-type-badge-${isBusiness ? 'business' : 'private'}`}
                        title={isBusiness ? 'Cliente Business (B2B)' : 'Cliente Privato (B2C)'}
                      >
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotCls}`} />
                        {label}
                      </span>
                    </div>
                    <p className="text-gray-500 text-[10px] truncate flex items-center gap-1.5">
                      <CountryFlag code={c.country} />
                      <span>{c.email} · creato {new Date(c.created_at).toLocaleDateString("it-IT")}</span>
                    </p>
                  </div>
                  <select
                    value={assignTo[c.id] || ""}
                    onChange={(e) => setAssignTo(prev => ({ ...prev, [c.id]: e.target.value }))}
                    className="bg-[#0A0A0A] border border-gray-700 rounded px-2 py-1 text-xs text-white min-w-[200px]"
                    data-testid={`orphan-select-dealer-${c.id}`}
                  >
                    <option value="">-- Seleziona dealer --</option>
                    {dealers.map(d => (
                      <option key={d.id} value={d.id}>
                        {d.first_name} {d.last_name} ({d.email})
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleAssign(c.id)}
                    disabled={assigning === c.id || !assignTo[c.id]}
                    className="px-3 py-1.5 rounded-lg bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold flex items-center gap-2 disabled:opacity-40"
                    data-testid={`orphan-assign-${c.id}`}
                  >
                    <Send size={11} />
                    {assigning === c.id ? "..." : "Assegna"}
                  </button>
                </div>
              </div>
            );
          })}
          {orphans.count < orphans.total && (
            <p className="text-gray-600 text-xs text-center pt-2">
              + altri {orphans.total - orphans.count} clienti orfani (limite 200 per query)
            </p>
          )}
        </div>
      )}

      {/* Operational note */}
      <div className="mt-4 pt-3 border-t border-gray-800 flex items-start gap-2">
        <AlertTriangle size={14} className="text-lojack-clause-500 flex-shrink-0 mt-0.5" />
        <p className="text-gray-500 text-[11px] leading-relaxed">
          Ogni assegnazione viene loggata in <code>admin_audit</code> con l'identità dell'admin agente e il timestamp. L'azione non è reversibile da questa schermata: se serve, contattare lo sviluppatore di backend per la rettifica diretta del record.
        </p>
      </div>
    </div>
  );
};

export default OrphanCustomersWidget;
